import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { s, authHeaders, Toast, getODataRows } from './listPageStyles';
import { StatusBadge, RelationPicker, SearchableSelect } from './ui';
import { RELATION_CONFIGS } from './relationConfigs';
import { unwrapLookupItems } from './c360';
import { EttnQrButton } from './EttnQr';
import { DocumentPrintDialog } from './DocumentPrintDialog';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  ALLOCATION_SOURCE_LABELS, FT_API, transactionDetailRoute, invoiceStatusMeta, normalizeAllocation,
  fetchInvoiceWithAllocations, fmtMoney as fmtTRY, fmtDate as fmtTRDate,
  pick as fpick, toNum as ftoNum,
} from './finance/allocationShared';
import {
  API_BASE, EMPTY_FORM, EMPTY_DETAIL, INVOICE_VARIANTS, variantForType, invoiceBasePath, returnSubtypeLabel,
  lifecycleLabel, lifecycleTone, approvalLabel, approvalTone,
  toInt, toNullableInt, toDecimal, toNullableGuid, toNullableDate, dateInputValue,
  formatMoney, formatPercent, createClientId, recalcLine,
  displayDate, lookupLabel, resolvePaymentTypeCode, getCollectionSummary,
  orderMultipleViolation, sanitizeDecimal, fetchProductUnits, convertUnitPrice,
  fetchBoolSetting, SETTING_SHOW_PASSIVE_PRODUCTS,
  fetchDocumentDefaults, findCashPaymentType,
  resolveLineVat,
  applyVatFallback,
  productVatOf,
} from './invoiceShared';
import { useDistributorUser } from '../hooks/useDistributorUser';
import { usePageActions } from '../contexts/PermissionContext';
import { useStockBalances, StockCell } from './stockBalance';

/**
 * FATURA DETAY — V2 tasarımı.
 * ═══════════════════════════════════════════════════════════════════════════════════
 * V2 prototipi kabul edildi ve TÜM fatura ekranlarının detay sayfası oldu; eski
 * `InvoiceDetailPage` ile prototip menüsü (/invoices-v2) kaldırıldı. İş mantığı eski
 * ekranla BİREBİR AYNIDIR — aynı uçlar, aynı doğrulamalar, aynı SaveWithDetails
 * payload'ı; yalnız sunum katmanı değişir.
 *
 * <b>Sekiz varyanta birden hizmet eder</b> (satış · alış · iade ×4 · hizmet ×2). Varyant
 * yeni kayıtta <c>?variant=</c> query'sinden, mevcut kayıtta belgenin Type'ından çözülür;
 * kapat/sil/iptal dönüşü ve belge seri tipi (DocumentTip) buna bağlıdır — prototipteyken
 * ikisi de satış faturasına sabitlenmişti.
 *
 * Tasarımda karşılığı OLMAYAN alanlar (İsk-2..4, KDV kolonları, taslak kalem satırı,
 * tahsilat drawer'ı, silme/karar diyalogları, saha iadesi bandı, yazdırma) eski ekrandaki
 * hâliyle KORUNUR — yeni tasarım fonksiyon kaybettirmemelidir.
 *
 * Tasarım sabit paletlidir (dark tema token'larına bağlanmaz).
 */

// #241 Miktar etiketini sade göster (6, 12, 1.5 — gereksiz sıfır yok).
const qtyLabel = (n) => `${+n}`;

// ---- V2 tasarım paleti (fatura-detay tasarımından) ----
const C = {
  bg: '#F8F8FA',
  card: '#FBFCFF',
  cardBorder: '#E5EBFF',
  strong: '#1B2340',
  navy: '#282568',
  deep: '#0B0F37',
  muted: '#66758C',
  purple: '#6C28D7',
  teal: '#0D9BB7',
  blue: '#2763EA',
  amber: '#E9A233',
  amberSoft: '#FBF3DD',
  amberStrong: '#FFA927',
  danger: '#FF4A4A',
  dangerSoft: '#FEF5F6',
  tealSoft: '#E4F7FA',
  blueSoft: '#EBF2FF',
  zebra: '#F4F8FF',
  shadow: '0 6px 24px rgba(30,35,90,0.06)',
};

const st = {
  page: {
    display: 'flex', flexDirection: 'column',
    height: 'calc(100vh - var(--topbar-height, 64px))',
    minHeight: 0, background: C.bg,
    fontFamily: "'Poppins', system-ui, -apple-system, sans-serif",
  },
  topbar: {
    position: 'sticky', top: 0, zIndex: 30,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
    padding: '14px 22px', background: '#fff', borderBottom: `1px solid ${C.cardBorder}`, flexWrap: 'wrap',
  },
  topLeft: { display: 'flex', alignItems: 'center', gap: 13, minWidth: 0 },
  iconSquare: {
    width: 42, height: 42, borderRadius: 8, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: C.purple, color: '#fff', fontSize: 19,
  },
  kicker: { margin: 0, color: C.muted, fontSize: 12, fontWeight: 500, letterSpacing: 0.2 },
  title: { margin: 0, color: C.navy, fontSize: 20, fontWeight: 600, letterSpacing: -0.2, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  codeChip: {
    fontSize: 13, fontWeight: 600, padding: '3px 10px', borderRadius: 6,
    background: C.blueSoft, color: C.blue,
  },
  actions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  scroll: { flex: 1, minHeight: 0, overflow: 'auto', padding: '22px 24px 18px', display: 'flex', flexDirection: 'column', gap: 18 },

  // ① Üç renkli bilgi kartı
  cardRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 17, alignItems: 'stretch' },
  cardOuter: (color) => ({ background: color, borderRadius: 24, paddingTop: 5, boxShadow: C.shadow }),
  cardInner: (gradient) => ({
    background: gradient, border: '1px solid rgba(102,117,140,0.3)', borderRadius: 23,
    height: 'calc(100% - 5px)', boxSizing: 'border-box', padding: '24px 20px',
  }),
  cardHead: { display: 'flex', alignItems: 'center', gap: 13, marginBottom: 16 },
  cardIcon: (color) => ({
    width: 38, height: 38, borderRadius: 6, background: color, color: '#fff', fontSize: 18,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  }),
  cardTitle: (color) => ({ fontSize: 19, fontWeight: 600, color, letterSpacing: -0.2 }),
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px 15px' },
  grid1: { display: 'grid', gridTemplateColumns: '1fr', gap: 10 },
  field: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 },
  fieldWide: { gridColumn: '1 / -1' },
  label: { fontSize: 12, fontWeight: 500, color: C.muted, letterSpacing: 0.2, display: 'inline-flex', alignItems: 'center', gap: 5 },
  lockTag: { fontSize: 9, fontWeight: 700, color: C.purple, background: 'rgba(108,40,215,0.10)', borderRadius: 4, padding: '1px 4px' },

  // ② Daha Fazla barı
  moreBar: {
    display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer',
    padding: '18px 21px', background: C.card, border: '1px solid #E2E9FF', borderRadius: 17,
    boxShadow: '0 6px 24px rgba(30,35,90,0.05)', color: C.navy, fontSize: 15, fontWeight: 600,
  },
  moreSection: {
    background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 17,
    boxShadow: '0 6px 24px rgba(30,35,90,0.05)', padding: '20px 24px',
  },
  moreGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px 18px' },

  // ③ Panel (İlişkili Tahsilatlar / Kalemler)
  panel: {
    background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 17,
    boxShadow: '0 6px 24px rgba(30,35,90,0.05)', boxSizing: 'border-box',
  },
  panelHead: { display: 'flex', alignItems: 'center', gap: 14, padding: '22px 28px 0', flexWrap: 'wrap' },
  panelIcon: (bg, color) => ({
    width: 38, height: 38, borderRadius: 6, background: bg, color, fontSize: 17, fontWeight: 600,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  }),
  panelTitle: (color) => ({ fontSize: 19, fontWeight: 600, color, letterSpacing: -0.2 }),
  countPill: {
    height: 29, borderRadius: 5, background: C.tealSoft, color: C.teal, fontSize: 13, fontWeight: 500,
    display: 'inline-flex', alignItems: 'center', padding: '0 14px',
  },

  // İlişkili Tahsilatlar
  allocGrid: { display: 'grid', gridTemplateColumns: 'minmax(280px, 435px) minmax(320px, 1fr)', gap: 21, padding: '20px 28px 26px' },
  allocSummary: { background: C.amberSoft, borderRadius: 11, padding: 23, minHeight: 200, boxSizing: 'border-box' },
  allocStatus: (bg) => ({
    minWidth: 86, height: 30, borderRadius: 6, background: bg, color: '#fff', fontSize: 14, fontWeight: 700,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 12px',
  }),
  allocKicker: { fontSize: 13, fontWeight: 500, color: C.muted, letterSpacing: 0.3 },
  allocValue: { marginTop: 7, fontSize: 19, fontWeight: 700, color: C.strong },
  allocRule: { margin: '20px 0', height: 1, background: `linear-gradient(to left, ${C.amberSoft}, ${C.amberStrong} 19%, ${C.amberStrong} 80%, ${C.amberSoft})` },
  statRows: { display: 'grid', gridTemplateRows: 'repeat(3, minmax(64px, 1fr))', gap: 8 },
  statBox: (bg) => ({ background: bg, borderRadius: 11, padding: '0 24px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }),
  statLabel: (color) => ({ fontSize: 13, fontWeight: 700, color, letterSpacing: 0.3 }),
  statValue: { marginTop: 3, fontSize: 20, fontWeight: 700, color: C.strong },
  allocList: { padding: '0 28px 22px', display: 'flex', flexDirection: 'column', gap: 8 },
  allocRow: { display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap', fontSize: 13, color: C.strong },
  allocLink: { background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: C.blue, fontWeight: 600, fontSize: 13, fontFamily: 'inherit' },
  tagSoft: { fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 5, background: C.blueSoft, color: C.blue },
  tagGrey: { fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 5, background: 'rgba(102,117,140,0.14)', color: C.muted },
  tagAmber: { fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 5, background: 'rgba(255,169,39,0.18)', color: C.amber },
  note: { fontSize: 11, color: C.muted, fontStyle: 'italic', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },

  // ④ Kalemler
  itemsCard: {
    flex: 1, minHeight: 320, background: C.card, border: `1px solid #E7ECF9`, borderRadius: 17,
    boxShadow: '0 6px 24px rgba(30,35,90,0.05)', display: 'flex', flexDirection: 'column', overflow: 'hidden',
  },
  searchBox: {
    display: 'flex', alignItems: 'center', gap: 9, height: 38, borderRadius: 6, background: '#fff',
    border: '1px solid rgba(39,99,234,0.28)', padding: '0 13px', boxSizing: 'border-box',
  },
  searchInput: { border: 0, outline: 0, background: 'transparent', width: '100%', fontFamily: 'inherit', fontSize: 14, color: '#2F3542' },
  tableWrap: { flex: 1, minHeight: 0, overflow: 'auto', marginTop: 18 },
  table: { width: '100%', minWidth: 1320, borderCollapse: 'separate', borderSpacing: 0, fontSize: 14 },
  th: {
    position: 'sticky', top: 0, zIndex: 1, textAlign: 'right', padding: '0 8px', height: 62,
    background: C.navy, color: '#fff', fontSize: 13, fontWeight: 700, letterSpacing: 0.3,
    whiteSpace: 'nowrap',
  },
  thLeft: { textAlign: 'left' },
  td: { padding: '10px 8px', textAlign: 'right', color: '#2F3542', whiteSpace: 'nowrap', borderBottom: '1px solid rgba(102,117,140,0.18)' },
  tdLeft: { textAlign: 'left' },
  codeCell: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 150, height: 34,
    padding: '0 12px', borderRadius: 6, background: C.blueSoft, color: C.blue, fontSize: 14, fontWeight: 600,
  },
  netCell: { fontSize: 15, fontWeight: 700, color: C.deep },
  cellInput: {
    height: 34, padding: '4px 8px', textAlign: 'right', borderRadius: 6, width: '100%', minWidth: 56,
    border: '1px solid rgba(39,99,234,0.28)', background: '#fff', fontFamily: 'inherit', fontSize: 14, color: '#2F3542', outline: 'none',
  },
  iskInput: {
    height: 34, padding: '4px 5px', textAlign: 'right', borderRadius: 6, width: 54, minWidth: 46,
    border: '1px solid rgba(39,99,234,0.28)', background: '#fff', fontFamily: 'inherit', fontSize: 14, color: '#2F3542', outline: 'none',
  },

  // ⑤ Alt sabit toplam barı
  totbar: {
    position: 'sticky', bottom: 0, zIndex: 30, display: 'flex', alignItems: 'center',
    background: C.deep, boxShadow: '0 -10px 30px rgba(11,15,55,0.18)', padding: '14px 16px 14px 0', gap: 12, flexWrap: 'wrap',
  },
  totCells: { flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', flexWrap: 'wrap' },
  totCell: (first) => ({
    padding: first ? '0 20px 0 34px' : '0 18px 0 30px', minWidth: 130,
    borderLeft: first ? 'none' : '1px solid rgba(255,255,255,0.22)',
  }),
  totLabel: { fontSize: 11, fontWeight: 600, color: '#C9CEE6', letterSpacing: 0.4, whiteSpace: 'nowrap' },
  totValue: { marginTop: 4, fontSize: 20, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' },
  grandCell: {
    flex: '0 0 auto', minWidth: 160, padding: '10px 18px', borderRadius: 12,
    background: 'linear-gradient(140deg,#00DFFF,#6C28D7)', color: '#fff',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
  },
};

// V2 buton stilleri (tasarımın yuvarlak/temiz diliyle; .dps ağacına bağlı değil).
const btn = {
  base: {
    height: 38, padding: '0 16px', borderRadius: 8, fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
    cursor: 'pointer', border: '1px solid rgba(102,117,140,0.32)', background: '#fff', color: C.navy,
  },
};
const btnSecondary = btn.base;
const btnPrimary = { ...btn.base, background: C.purple, borderColor: C.purple, color: '#fff' };
const btnDangerV2 = { ...btn.base, borderColor: C.danger, color: C.danger };
const rowDeleteBtn = { ...btn.base, height: 30, minWidth: 32, padding: '0 8px', borderColor: C.danger, color: C.danger, fontSize: 15, lineHeight: 1 };

// Tasarımın Poppins tipografisi — yalnız bu prototip açıkken yüklenir.
const V2_FONT_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
.inv2 input::placeholder, .inv2 textarea::placeholder { color: #98A2B8; }
.inv2 tbody tr:nth-child(even) { background: ${C.zebra}; }
`;

// Bellekte yüklü küçük lookup'lar için aranabilir select fetch'i.
const localFetch = (items, getLabel) => (query) => {
  const q = String(query || '').toLocaleLowerCase('tr');
  return Promise.resolve(
    items
      .map((it) => ({ value: it.id, label: getLabel(it.id) }))
      .filter((o) => !q || String(o.label).toLocaleLowerCase('tr').includes(q))
      .slice(0, 100),
  );
};

// Salt-okunur alan kutusu (tasarımın beyaz zemin + renkli çerçeve dili).
const viewBox = (accent) => ({
  height: 38, padding: '0 12px', borderRadius: 6, background: '#fff',
  border: `1px solid ${accent}`, color: C.strong, fontSize: 14, fontFamily: 'inherit',
  width: '100%', boxSizing: 'border-box', opacity: 0.85,
});
const editBox = (accent) => ({ ...viewBox(accent), opacity: 1 });

export function InvoiceDetailV2Page() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new';

  // Yeni kayıtta variant query'den (liste new butonu ?variant=... ile yönlendirir).
  const [variant, setVariant] = useState(searchParams.get('variant') || 'sales');
  const cfg = INVOICE_VARIANTS[variant] || INVOICE_VARIANTS.sales;
  const defaultType = cfg.types[0];
  // Varyanta karşılık gelen liste sayfası (kapat/sil/iptal sonrası dönüş için).
  const listPath = invoiceBasePath(variant);
  // variant → DocumentSeries.DocumentTip (0=Satış, 1=Alış, 2=İade, 3=Hizmet).
  const documentTip = {
    sales: 0, purchase: 1,
    return: 2, returnGood: 2, returnDamaged: 2, returnVendor: 2,
    serviceGiven: 3, serviceReceived: 3,
  }[variant] ?? 0;
  // #835 — İade türü MENÜDEN gelir; formda seçilmez, yalnız künye olarak gösterilir.
  const isReturnVariant = cfg.types.every((t) => t === 2 || t === 3 || t === 4);

  usePageMeta(isNew ? 'Yeni Fatura' : 'Fatura Detay', cfg.kicker);

  // Menü aksiyon yetkileri — usePageActions argümansız çağrılır, aktif rota pathname'inden
  // menuId'yi kendi çözer (bu bileşen /invoices, /purchase-invoices, /return-invoices*,
  // /service-invoices-given ve /service-invoices-received rotalarına hizmet eder; her rota
  // kendi menüsünün hakkına düşer).
  const { canAdd, canEdit, canDelete } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;

  const [mode, setMode] = useState(isNew ? 'new' : (searchParams.get('edit') === '1' ? 'edit' : 'view'));
  const [editFromUrl, setEditFromUrl] = useState(!isNew && searchParams.get('edit') === '1');
  const isView = mode === 'view' || (!isNew && !canEdit);
  const [loading, setLoading] = useState(!isNew);
  const [form, setForm] = useState(EMPTY_FORM);
  const [ettn, setEttn] = useState('');
  const [invoiceDetails, setInvoiceDetails] = useState([]);
  const [deletedDetailIds, setDeletedDetailIds] = useState([]);
  const [toast, setToast] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [paymentDrawer, setPaymentDrawer] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [collDecision, setCollDecision] = useState(null);
  const [printOpen, setPrintOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [colSearch, setColSearch] = useState({ code: '', name: '' });

  // Lookup'lar
  const [representatives, setRepresentatives] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const distributorUser = useDistributorUser();
  const [customerLabels, setCustomerLabels] = useState({});
  const [productLabels, setProductLabels] = useState({});
  const [productMinQty, setProductMinQty] = useState({});
  const [productUnits, setProductUnits] = useState({});
  const [showPassiveProducts, setShowPassiveProducts] = useState(false);
  const productConfig = useMemo(
    () => (showPassiveProducts ? RELATION_CONFIGS.productWithGroups : { ...RELATION_CONFIGS.productWithGroups, baseFilter: 'Status ne 0' }),
    [showPassiveProducts],
  );
  const [payments, setPayments] = useState([]);

  // Saha iadesinin muhasebe projeksiyonu mu? (V1 ile aynı kural.)
  const [fieldReturn, setFieldReturn] = useState(null);

  // İlişkili tahsilatlar
  const [allocations, setAllocations] = useState([]);
  const [returnInvoices, setReturnInvoices] = useState([]);
  const [allocSources, setAllocSources] = useState({});
  const [finance, setFinance] = useState({
    netAmount: 0, allocatedAmount: 0, returnedAmount: 0, openAmount: 0, invoiceStatus: 0,
  });

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };

  // Hızlı ekleme satırı + odak zinciri
  const [draft, setDraft] = useState({ ...EMPTY_DETAIL, clientId: 'draft' });
  const qtyRef = useRef(null);
  const unitPriceRef = useRef(null);
  const isk1Ref = useRef(null);
  const tableWrapRef = useRef(null);
  const [productPickerOpenToken, setProductPickerOpenToken] = useState(0);

  const stockProductIds = useMemo(
    () => [...invoiceDetails.map((l) => l.productId), draft.productId],
    [invoiceDetails, draft.productId],
  );
  const { balanceOf: stockBalanceOf, ready: stockReady } =
    useStockBalances(form.vendorId, form.warehouseId, stockProductIds);

  // ---- Küçük lookup'ları yükle ----
  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/representatives/Select', { headers }).then((r) => r.ok ? r.json() : []),
      fetch('/api/vendors/Select', { headers }).then((r) => r.ok ? r.json() : []),
      fetch('/api/warehouses/Select', { headers }).then((r) => r.ok ? r.json() : []),
      fetch('/api/paymenttypes/Select', { headers }).then((r) => r.ok ? r.json() : []),
    ]).then(([rep, ven, wh, pt]) => {
      setRepresentatives(unwrapLookupItems(rep));
      setVendors(unwrapLookupItems(ven));
      setWarehouses(unwrapLookupItems(wh));
      setPaymentTypes(unwrapLookupItems(pt));
    }).catch(() => {});
    fetchBoolSetting(SETTING_SHOW_PASSIVE_PRODUCTS, authHeaders(), false).then(setShowPassiveProducts);
  }, []);

  const nameOf = (items, idValue) => {
    if (!idValue) return '-';
    const item = items.find((x) => x.id === idValue);
    return item ? lookupLabel(item) : '-';
  };
  const representativeName = useCallback((v) => nameOf(representatives, v), [representatives]);
  const vendorName = useCallback((v) => nameOf(vendors, v), [vendors]);
  const warehouseName = useCallback((v) => nameOf(warehouses, v), [warehouses]);
  const customerName = useCallback((v) => (v ? (customerLabels[v] || '…') : '-'), [customerLabels]);
  const productName = useCallback((v) => (v ? (productLabels[v] || '…') : '-'), [productLabels]);
  const renderStockCell = (productId, unitName) => (
    <StockCell
      productId={productId}
      balanceOf={stockBalanceOf}
      ready={stockReady}
      unitMultiplier={unitsOf(productId).find((u) => u.name === unitName)?.multiplier || 1}
    />
  );
  const getPaymentTypeLabel = useCallback((v) => {
    const item = paymentTypes.find((p) => p.id === v);
    return item ? lookupLabel(item) : '-';
  }, [paymentTypes]);
  const paymentTypeIdByCode = useCallback((code) => {
    const n = Number.parseInt(code, 10);
    if (Number.isNaN(n)) return '';
    const item = paymentTypes.find((e) => resolvePaymentTypeCode(e) === n);
    return item?.id || '';
  }, [paymentTypes]);
  const representativeRegion = useCallback((v) => {
    const item = representatives.find((x) => x.id === v);
    return item ? (item.regionName || item.RegionName || item.description || item.Description || item.groupName || item.GroupName || '') : '';
  }, [representatives]);

  const vendorFetch = useMemo(() => localFetch(vendors, vendorName), [vendors, vendorName]);
  const scopedWarehouses = useMemo(
    () => (form.vendorId ? warehouses.filter((w) => String(w.vendorId ?? w.VendorId ?? '') === String(form.vendorId)) : []),
    [warehouses, form.vendorId],
  );
  const scopedReps = useMemo(
    () => (form.vendorId ? representatives.filter((r) => String(r.vendorId ?? r.VendorId ?? '') === String(form.vendorId)) : []),
    [representatives, form.vendorId],
  );
  const warehouseFetch = useMemo(() => localFetch(scopedWarehouses, warehouseName), [scopedWarehouses, warehouseName]);
  const repFetch = useMemo(() => localFetch(scopedReps, representativeName), [scopedReps, representativeName]);
  const customerConfig = useMemo(
    () => (form.vendorId
      ? { ...RELATION_CONFIGS.customer, baseFilter: `VendorId eq ${form.vendorId}` }
      : RELATION_CONFIGS.customer),
    [form.vendorId],
  );
  const changeVendor = (val) => setForm((c) => (
    String(c.vendorId ?? '') === String(val ?? '')
      ? c
      : { ...c, vendorId: val, customerId: '', warehouseId: '', representativeId: '' }
  ));
  const changeWarehouse = (val) => {
    setValue('warehouseId', val);
    setInvoiceDetails((cur) => cur.map((row) => (
      String(row.warehouseId ?? '') === String(val ?? '') ? row : { ...row, warehouseId: val || '' }
    )));
  };
  const paymentTypeFetch = useMemo(() => localFetch(paymentTypes, getPaymentTypeLabel), [paymentTypes, getPaymentTypeLabel]);

  const ensureCustomerLabel = useCallback(async (cid) => {
    if (!cid || customerLabels[cid] !== undefined) return;
    try {
      const url = `/api/customers?$top=1&$filter=${encodeURIComponent(`Id eq ${cid}`)}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (res.ok) { const rows = getODataRows(await res.json()); if (rows[0]) setCustomerLabels((p) => ({ ...p, [cid]: lookupLabel(rows[0]) })); }
    } catch { /* sessiz */ }
  }, [customerLabels]);

  // Talep 833 — Müşteri seçilince Temsilci rut ilişkisinden, Depo temsilcinin satış deposundan.
  const applyRouteDefaults = useCallback(async (customerId) => {
    if (!customerId) return;
    const d = await fetchDocumentDefaults(customerId, authHeaders());
    if (!d) return;
    if (d.representativeId && d.representativeName) {
      setRepresentatives((prev) => (prev.some((x) => x.id === d.representativeId)
        ? prev
        : [...prev, { id: d.representativeId, textId: d.representativeTextId, name: d.representativeName }]));
    }
    setForm((c) => {
      const next = { ...c, representativeId: d.representativeId || '' };
      if (d.salesWarehouseId) next.warehouseId = d.salesWarehouseId;
      return next;
    });
  }, []);

  // #284 Müşteri kartı varsayılanları (DIST / Vade / Ana Depo).
  const applyCustomerDefaults = useCallback(async (customerId, item) => {
    if (!customerId) return;
    let cust = item;
    const missing = (o) => !o || (o.vendorId === undefined && o.VendorId === undefined
      && o.dueDate === undefined && o.DueDate === undefined);
    if (missing(cust)) {
      try {
        const url = `/api/customers?$top=1&$filter=${encodeURIComponent(`Id eq ${customerId}`)}`;
        const res = await fetch(url, { headers: authHeaders() });
        if (res.ok) cust = getODataRows(await res.json())[0];
      } catch { /* sessiz: alanlar elle girilebilir */ }
    }
    if (!cust) return;
    const custVendorId = cust.vendorId ?? cust.VendorId ?? '';
    const days = Number.parseInt(cust.dueDate ?? cust.DueDate, 10);
    setForm((c) => {
      const next = { ...c };
      if (custVendorId && !distributorUser.isDistributor) next.vendorId = custVendorId;
      if (!Number.isNaN(days)) {
        next.dueDay = String(days);
        const base = next.documentDate ? new Date(next.documentDate) : new Date();
        if (!Number.isNaN(base.getTime())) { base.setDate(base.getDate() + days); next.dueDate = dateInputValue(base); }
      }
      if (custVendorId) {
        const central = warehouses.find((w) => String(w.vendorId ?? w.VendorId ?? '') === String(custVendorId)
          && String(w.type ?? w.Type ?? '').toLowerCase().includes('ana'));
        if (central) next.warehouseId = central.id;
      }
      return next;
    });
  }, [warehouses, distributorUser.isDistributor]);

  // Sıra ÖNEMLİ: önce kart varsayılanları, sonra rut varsayılanları (daha özel kural).
  const applyCustomerSelection = useCallback(async (customerId, item) => {
    if (!customerId) return;
    await applyCustomerDefaults(customerId, item);
    await applyRouteDefaults(customerId);
  }, [applyCustomerDefaults, applyRouteDefaults]);

  const ensureProductLabels = useCallback(async (ids) => {
    const need = [...new Set(ids.filter((x) => x && productLabels[x] === undefined))];
    if (!need.length) return;
    try {
      const filter = need.map((x) => `Id eq ${x}`).join(' or ');
      const url = `/api/products?$top=500&light=1&$filter=${encodeURIComponent(`(${filter})`)}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (res.ok) {
        const found = {}; const mins = {}; const bases = {};
        getODataRows(await res.json()).forEach((p) => { found[p.id] = lookupLabel(p); mins[p.id] = p.minOrderQuantity; bases[p.id] = p.unit1; });
        setProductLabels((prev) => { const next = { ...prev }; need.forEach((x) => { next[x] = found[x] || '-'; }); return next; });
        setProductMinQty((prev) => { const next = { ...prev }; need.forEach((x) => { if (mins[x] != null) next[x] = mins[x]; }); return next; });
        const headers = authHeaders();
        need.forEach((pid) => {
          fetchProductUnits(pid, headers, bases[pid]).then((units) => setProductUnits((prev) => (prev[pid] ? prev : { ...prev, [pid]: units })));
        });
      }
    } catch { /* sessiz */ }
  }, [productLabels]);

  const rowToForm = useCallback((row) => ({
    textId: row.textId || '', printedNo: row.printedNo || '', printedType: row.printedType ?? '',
    receiptNo: row.receiptNo || '', erpId: row.erpId || '', specialId: row.specialId || '',
    customerId: row.customerId || '', representativeId: row.representativeId || '', vendorId: row.vendorId || '', warehouseId: row.warehouseId || '',
    deliveryAdress: row.deliveryAdress || '', description1: row.description1 || '', description2: row.description2 || '',
    documentDate: dateInputValue(row.documentDate), dueDate: dateInputValue(row.dueDate), shipDate: dateInputValue(row.shipDate),
    dueDay: row.dueDay ?? '', year: row.year ?? '', type: row.type ?? 0,
    paymentType: row.paymentType ?? '', paymentTypeId: paymentTypeIdByCode(row.paymentType),
    eArchive: row.eArchive ?? '', status: row.status ?? '',
    lifecycleStatus: row.lifecycleStatus ?? '', approvalStatus: row.approvalStatus ?? 0,
    grossAmount: row.grossAmount ?? '', netAmount: row.netAmount ?? '', discountAmount: row.discountAmount ?? '',
    discountPercentage1: row.discountPercentage1 ?? '', discountPercentage2: row.discountPercentage2 ?? '', discountPercentage3: row.discountPercentage3 ?? '',
    vatBase: row.vatBase ?? '', vatAmount: row.vatAmount ?? '', specialConsumptionTaxAmount: row.specialConsumptionTaxAmount ?? '',
    orderId: row.orderId || '', dispatchId: row.dispatchId || '',
  }), [paymentTypeIdByCode]);

  const rowToDetail = (row) => {
    const quantity = toDecimal(row.quantity);
    const unitPrice = toDecimal(row.unitPrice);
    const gross = quantity * unitPrice;
    const discountAmount = toDecimal(row.discountAmount);
    const noPct = [1, 2, 3, 4].every((n) => toDecimal(row[`discountPercentage${n}`]) === 0);
    let pct1 = row.discountPercentage1 ?? '';
    if (noPct && gross > 0 && discountAmount > 0) {
      pct1 = String(Number(((discountAmount / gross) * 100).toFixed(2)));
    }
    return recalcLine({
      id: row.id || '', clientId: row.id || createClientId(),
      productId: row.productId || '', warehouseId: row.warehouseId || '',
      quantity: row.quantity ?? '', unitOrder: row.unitOrder ?? '', unit: row.unit ?? '',
      unitPrice: row.unitPrice ?? '', unitNetPrice: row.unitNetPrice ?? '', unitVatPrice: row.unitVatPrice ?? '',
      grossAmount: row.grossAmount ?? '', netAmount: row.netAmount ?? '', discountAmount: row.discountAmount ?? '',
      discountPercentage1: pct1,
      discountPercentage2: row.discountPercentage2 ?? '', discountPercentage3: row.discountPercentage3 ?? '',
      discountPercentage4: row.discountPercentage4 ?? '', discountPercentage5: row.discountPercentage5 ?? '',
      discountPercentage6: row.discountPercentage6 ?? '', discountPercentage7: row.discountPercentage7 ?? '',
      discountPercentage8: row.discountPercentage8 ?? '',
      vatBase: row.vatBase ?? '', vatAmount: row.vatAmount ?? '', vatPercentage: row.vatPercentage ?? '',
    });
  };

  const loadAllocations = useCallback(async (invoiceId) => {
    try {
      const inv = await fetchInvoiceWithAllocations(invoiceId);
      const allocs = (fpick(inv, 'collectionAllocations', 'CollectionAllocations') || []).map(normalizeAllocation);
      setAllocations(allocs);
      setReturnInvoices((fpick(inv, 'returnInvoices', 'ReturnInvoices') || []).map((r) => ({
        id: fpick(r, 'id', 'Id'),
        invoiceNo: fpick(r, 'textId', 'TextId') || '-',
        documentDate: fpick(r, 'documentDate', 'DocumentDate'),
        netAmount: ftoNum(fpick(r, 'netAmount', 'NetAmount')),
      })));
      setFinance({
        netAmount: ftoNum(fpick(inv, 'netAmount', 'NetAmount')),
        allocatedAmount: ftoNum(fpick(inv, 'allocatedAmount', 'AllocatedAmount')),
        returnedAmount: ftoNum(fpick(inv, 'returnedAmount', 'ReturnedAmount')),
        openAmount: ftoNum(fpick(inv, 'openAmount', 'OpenAmount')),
        invoiceStatus: ftoNum(fpick(inv, 'invoiceStatus', 'InvoiceStatus')),
      });

      const headers = authHeaders();
      const map = {};
      const ftIds = [...new Set(allocs.map((a) => a.financeTransactionId).filter(Boolean))];
      const payIds = [...new Set(allocs.map((a) => a.paymentId).filter(Boolean))];
      if (ftIds.length) {
        const f = ftIds.map((x) => `Id eq ${x}`).join(' or ');
        const r = await fetch(`${FT_API}?$filter=${encodeURIComponent(f)}&$top=${ftIds.length}`, { headers });
        if (r.ok) {
          getODataRows(await r.json()).forEach((t) => {
            const tid = fpick(t, 'id', 'Id');
            map[`ft:${tid}`] = {
              id: tid,
              label: fpick(t, 'documentNo', 'DocumentNo') || 'Tahsilat',
              date: fpick(t, 'transactionDate', 'TransactionDate'),
              method: ftoNum(fpick(t, 'method', 'Method')),
              direction: ftoNum(fpick(t, 'direction', 'Direction')),
            };
          });
        }
      }
      if (payIds.length) {
        const f = payIds.map((x) => `Id eq ${x}`).join(' or ');
        const r = await fetch(`/api/payments?$filter=${encodeURIComponent(f)}&$top=${payIds.length}`, { headers });
        if (r.ok) {
          getODataRows(await r.json()).forEach((p) => {
            const pid = fpick(p, 'id', 'Id');
            map[`pay:${pid}`] = {
              id: pid,
              label: fpick(p, 'textId', 'TextId') || fpick(p, 'receiptNo', 'ReceiptNo') || 'Ödeme',
              date: fpick(p, 'paymentDate', 'PaymentDate'),
              legacy: true,
            };
          });
        }
      }
      setAllocSources(map);
    } catch { /* eşleştirme bilgisi kritik değil — sayfa yine açılır */ }
  }, []);

  // ---- Faturayı yükle ----
  const load = useCallback(async () => {
    if (isNew) {
      const now = new Date();
      setForm({ ...EMPTY_FORM, type: defaultType, documentDate: dateInputValue(now), shipDate: dateInputValue(now), year: now.getFullYear().toString() });
      setInvoiceDetails([]); setDeletedDetailIds([]); setLoading(false);
      setFieldReturn(null);
      setAllocations([]); setReturnInvoices([]); setAllocSources({});
      setFinance({ netAmount: 0, allocatedAmount: 0, returnedAmount: 0, openAmount: 0, invoiceStatus: 0 });
      return;
    }
    setLoading(true);
    try {
      const headers = authHeaders();
      const invUrl = `/api/invoices?$top=1&$filter=${encodeURIComponent(`Id eq ${id}`)}`;
      const invRes = await fetch(invUrl, { headers });
      if (!invRes.ok) throw new Error('Fatura alınamadı');
      const invRow = getODataRows(await invRes.json())[0];
      if (!invRow) throw new Error('Fatura bulunamadı');
      setForm(rowToForm(invRow));
      setEttn(invRow.ettn || invRow.Ettn || '');
      setVariant(variantForType(invRow.type));
      if (invRow.customerId) ensureCustomerLabel(invRow.customerId);

      if (Number(invRow.type ?? invRow.Type) === 3) {
        try {
          const rRes = await fetch(`/api/returns/GetById?id=${id}`, { headers });
          const rBody = rRes.ok ? await rRes.json() : null;
          const rHead = (rBody?.data ?? rBody)?.header;
          setFieldReturn(rHead?.id ? rHead : null);
        } catch { setFieldReturn(null); }
      } else {
        setFieldReturn(null);
      }

      const detUrl = `/api/invoicedetails?$filter=${encodeURIComponent(`InvoiceId eq ${id}`)}&$orderby=${encodeURIComponent('LineOrder asc,Id asc')}`;
      const detRes = await fetch(detUrl, { headers });
      const details = detRes.ok ? getODataRows(await detRes.json()).map(rowToDetail) : [];
      setInvoiceDetails(details);
      ensureProductLabels(details.map((d) => d.productId).filter(Boolean));

      const payUrl = `/api/payments?$filter=${encodeURIComponent(`InvoiceId eq ${id}`)}&$orderby=${encodeURIComponent('PaymentDate desc')}`;
      const payRes = await fetch(payUrl, { headers });
      setPayments(payRes.ok ? getODataRows(await payRes.json()) : []);

      loadAllocations(id);
    } catch (e) {
      showToast('error', e.message || 'Fatura alınamadı');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew, loadAllocations]);

  useEffect(() => { load(); }, [load]);

  // Projeksiyon faturası elle düzenlenemez.
  useEffect(() => {
    if (fieldReturn && mode !== 'view') setMode('view');
  }, [fieldReturn, mode]);

  useEffect(() => {
    if (!paymentTypes.length || form.paymentTypeId || form.paymentType === '') return;
    const mapped = paymentTypeIdByCode(form.paymentType);
    if (mapped) setForm((c) => (c.paymentTypeId ? c : { ...c, paymentTypeId: mapped }));
  }, [paymentTypes, form.paymentTypeId, form.paymentType, paymentTypeIdByCode]);

  useEffect(() => {
    if (!isNew || !distributorUser.isDistributor || !distributorUser.vendorId) return;
    setForm((c) => (c.vendorId === distributorUser.vendorId ? c : { ...c, vendorId: distributorUser.vendorId }));
  }, [isNew, distributorUser.isDistributor, distributorUser.vendorId]);

  useEffect(() => {
    if (!isNew) return;
    if (paymentTypes.length && !form.paymentTypeId) {
      const pesin = findCashPaymentType(paymentTypes);
      if (pesin) setForm((c) => (c.paymentTypeId ? c : { ...c, paymentTypeId: pesin.id, paymentType: 1 }));
    }
    if (warehouses.length && !form.warehouseId) {
      const ana = warehouses.find((w) => String(w.type ?? w.Type ?? '').toLowerCase().includes('ana'));
      if (ana) setForm((c) => (c.warehouseId ? c : { ...c, warehouseId: ana.id }));
    }
  }, [isNew, paymentTypes, warehouses, form.paymentTypeId, form.warehouseId]);

  const setValue = (key, value) => setForm((c) => ({ ...c, [key]: value }));

  // #246 Vade Günü ↔ Vade Tarihi çift-yönlü senkron.
  const daysBetween = (from, to) => {
    const a = new Date(from); a.setHours(0, 0, 0, 0);
    const b = new Date(to); b.setHours(0, 0, 0, 0);
    return Math.round((b.getTime() - a.getTime()) / 86400000);
  };
  const docBaseDate = (docStr) => {
    const d = docStr ? new Date(docStr) : new Date();
    return Number.isNaN(d.getTime()) ? new Date() : d;
  };
  const onDueDayChange = (val) => {
    const days = Number.parseInt(val, 10);
    if (val === '' || Number.isNaN(days)) { setValue('dueDay', val); return; }
    const due = docBaseDate(form.documentDate); due.setDate(due.getDate() + days);
    setForm((c) => ({ ...c, dueDay: String(days), dueDate: dateInputValue(due) }));
  };
  const onDueDateChange = (val) => {
    if (!val) { setForm((c) => ({ ...c, dueDate: '' })); return; }
    const diff = daysBetween(docBaseDate(form.documentDate), val);
    setForm((c) => ({ ...c, dueDate: val, dueDay: Number.isFinite(diff) ? String(diff) : c.dueDay }));
  };
  const onDocDateChange = (val) => {
    setForm((c) => {
      const next = { ...c, documentDate: val };
      const days = Number.parseInt(c.dueDay, 10);
      if (val && !Number.isNaN(days)) {
        const due = new Date(val);
        if (!Number.isNaN(due.getTime())) { due.setDate(due.getDate() + days); next.dueDate = dateInputValue(due); }
      }
      if (val && (!c.shipDate || c.shipDate === c.documentDate)) next.shipDate = val;
      return next;
    });
  };

  // ---- Toplamlar ----
  const computeTotals = (rows) => {
    const dt = rows.reduce((acc, row) => ({
      grossAmount: acc.grossAmount + toDecimal(row.grossAmount),
      netAmount: acc.netAmount + toDecimal(row.netAmount),
      discountAmount: acc.discountAmount + toDecimal(row.discountAmount),
      vatBase: acc.vatBase + toDecimal(row.vatBase),
      vatAmount: acc.vatAmount + toDecimal(row.vatAmount),
    }), { grossAmount: 0, netAmount: 0, discountAmount: 0, vatBase: 0, vatAmount: 0 });
    const any = rows.length > 0;
    return {
      grossAmount: any ? dt.grossAmount : toDecimal(form.grossAmount),
      netAmount: any ? dt.netAmount : toDecimal(form.netAmount),
      discountAmount: any ? dt.discountAmount : toDecimal(form.discountAmount),
      vatBase: any ? dt.vatBase : toDecimal(form.vatBase),
      vatAmount: any ? dt.vatAmount : toDecimal(form.vatAmount),
      sct: toDecimal(form.specialConsumptionTaxAmount),
    };
  };
  const totals = computeTotals(invoiceDetails);

  const paidTotal = payments.reduce((sum, p) => sum + toDecimal(p.amount ?? p.Amount), 0);
  const collection = getCollectionSummary(totals.netAmount, paidTotal, payments, form.dueDate);

  // ---- Kalem işlemleri ----
  const updateDetailRow = (clientId, key, value) => {
    setInvoiceDetails((cur) => cur.map((row) => {
      if (row.clientId !== clientId) return row;
      const next = { ...row, [key]: value };
      const calcKeys = ['quantity', 'unitPrice', 'vatPercentage', 'discountPercentage1', 'discountPercentage2', 'discountPercentage3', 'discountPercentage4'];
      return calcKeys.includes(key) ? recalcLine(next) : next;
    }));
  };

  const removeDetailRow = (row) => {
    if (row.id) setDeletedDetailIds((cur) => (cur.includes(row.id) ? cur : [...cur, row.id]));
    setInvoiceDetails((cur) => cur.filter((x) => x.clientId !== row.clientId));
  };

  const unitsOf = (pid) => productUnits[pid] || [];
  const renderUnitCell = (pid, unitVal, onChange) => {
    const opts = unitsOf(pid);
    const names = opts.map((u) => u.name);
    const cur = unitVal || names[0] || '';
    if (isView) return cur || '-';
    return (
      <select style={{ ...st.cellInput, textAlign: 'left', minWidth: 68 }} value={cur} onChange={(e) => onChange(e.target.value)}>
        {names.length === 0 && <option value="">-</option>}
        {names.map((u) => <option key={u} value={u}>{u}</option>)}
        {cur && !names.includes(cur) && <option value={cur}>{cur}</option>}
      </select>
    );
  };

  const changeRowUnit = (row, newUnit) => {
    const units = unitsOf(row.productId);
    const newPrice = convertUnitPrice(row.unitPrice, units, row.unit || units[0]?.name, newUnit);
    setInvoiceDetails((cur) => cur.map((r) => (r.clientId === row.clientId ? recalcLine({ ...r, unit: newUnit, unitPrice: newPrice }) : r)));
  };
  const changeDraftUnit = (newUnit) => {
    const units = unitsOf(draft.productId);
    const newPrice = convertUnitPrice(draft.unitPrice, units, draft.unit || units[0]?.name, newUnit);
    setDraft((d) => recalcLine({ ...d, unit: newUnit, unitPrice: newPrice }));
  };

  const fetchEffectivePriceInto = async (productId, applyFn, fallbackVat) => {
    if (!productId) return;
    try {
      const params = new URLSearchParams({ productId });
      if (form.customerId) params.append('customerId', form.customerId);
      if (form.vendorId) params.append('vendorId', form.vendorId);
      const res = await fetch(`/api/prices/Effective?${params}`, { headers: authHeaders() });
      if (!res.ok) {
        applyVatFallback(applyFn, fallbackVat);
        return;
      }
      const price = await res.json();
      if (!price) {
        showToast('error', 'Bu ürün için tanımlı fiyat yok — manuel giriniz');
        applyVatFallback(applyFn, fallbackVat);
        return;
      }
      const patch = { unitPrice: String(price.salePrice ?? 0) };
      const vat = resolveLineVat(price, fallbackVat);
      if (vat != null) patch.vatPercentage = vat;
      applyFn(patch);
    } catch {
      applyVatFallback(applyFn, fallbackVat);
    }
  };

  const setDraftProduct = (val, item) => {
    if (val && item) setProductLabels((p) => ({ ...p, [val]: lookupLabel(item) }));
    if (val && item && item.minOrderQuantity != null) setProductMinQty((p) => ({ ...p, [val]: item.minOrderQuantity }));
    setDraft((d) => recalcLine({ ...d, productId: val, unit: '' }));
    if (val) {
      const duo = Number(item?.unit);
      const defaultUnitName = duo >= 1 && duo <= 5 ? String(item?.[`unit${duo}`] ?? '').trim() : '';
      fetchProductUnits(val, authHeaders(), item?.unit1).catch(() => []).then((units) => {
        setProductUnits((p) => ({ ...p, [val]: units }));
        const base = units[0]?.name || '';
        const wanted = defaultUnitName && units.some((u) => u.name === defaultUnitName) ? defaultUnitName : base;
        setDraft((d) => (d.productId === val && !d.unit ? { ...d, unit: wanted } : d));
        fetchEffectivePriceInto(
          val,
          (patch) => setDraft((d) => {
            if (d.productId !== val) return d;
            const scaledPrice = wanted && wanted !== base
              ? convertUnitPrice(patch.unitPrice, units, base, wanted)
              : patch.unitPrice;
            return recalcLine({ ...d, ...patch, ...(patch.unitPrice != null ? { unitPrice: scaledPrice } : {}) });
          }),
          productVatOf(item, variant === 'purchase'),
        );
      });
      ensureProductLabels([val]);
      setTimeout(() => qtyRef.current?.focus(), 60);
    }
  };

  const headerReady = !!(form.customerId && form.warehouseId);
  const headerHint = 'Önce müşteri ve depo seçin';

  const buildDraftRow = (existingCount) => {
    if (!form.customerId) { showToast('error', 'Önce müşteri seçin'); return null; }
    if (!form.warehouseId) { showToast('error', 'Önce depo seçin'); return null; }
    if (!draft.productId) { showToast('error', 'Önce ürün seçin'); return null; }
    if (toDecimal(draft.quantity) <= 0) { showToast('error', 'Miktar 0’dan büyük olmalı'); return null; }
    const minBase = orderMultipleViolation(draft.quantity, productMinQty[draft.productId]);
    if (minBase) {
      showToast('error', `${productName(draft.productId)}: yalnız ${qtyLabel(minBase)} ve katları girilebilir (örn. ${qtyLabel(minBase)}, ${qtyLabel(minBase * 2)}, ${qtyLabel(minBase * 3)}).`);
      return null;
    }
    return recalcLine({
      ...draft,
      clientId: createClientId(),
      warehouseId: form.warehouseId || '',
      lineOrder: (existingCount ?? invoiceDetails.length) + 1,
    });
  };

  const draftHasContent = () => Boolean(draft.productId);

  const commitDraft = () => {
    const row = buildDraftRow();
    if (!row) return;
    setInvoiceDetails((cur) => [...cur, row]);
    setDraft({ ...EMPTY_DETAIL, clientId: 'draft' });
    showToast('success', `Satır eklendi · ${productName(row.productId)}`);
    setTimeout(() => { const el = tableWrapRef.current; if (el) el.scrollTop = el.scrollHeight; }, 0);
    setProductPickerOpenToken((t) => t + 1);
  };

  // ---- Kaydet ----
  const buildPayload = (rows = invoiceDetails) => {
    const t = computeTotals(rows);
    const selectedPt = paymentTypes.find((x) => x.id === form.paymentTypeId);
    const code = resolvePaymentTypeCode(selectedPt);
    return {
      id: isNew ? '00000000-0000-0000-0000-000000000000' : id,
      textId: form.textId, printedNo: form.printedNo, printedType: toInt(form.printedType),
      receiptNo: form.receiptNo || null, erpId: form.erpId || null, specialId: form.specialId || null,
      customerId: form.customerId, representativeId: form.representativeId, vendorId: toNullableGuid(form.vendorId), warehouseId: form.warehouseId,
      deliveryAdress: form.deliveryAdress || null, description1: form.description1 || null, description2: form.description2 || null,
      documentDate: toNullableDate(form.documentDate), dueDate: toNullableDate(form.dueDate), shipDate: toNullableDate(form.shipDate),
      dueDay: toNullableInt(form.dueDay), year: toInt(form.year), type: toInt(form.type),
      paymentType: code ?? toInt(form.paymentType), paymentTypeId: toNullableGuid(form.paymentTypeId),
      eArchive: toInt(form.eArchive), status: toInt(form.status),
      lifecycleStatus: toInt(form.lifecycleStatus), approvalStatus: toInt(form.approvalStatus),
      grossAmount: t.grossAmount, netAmount: t.netAmount, discountAmount: t.discountAmount,
      discountPercentage1: toDecimal(form.discountPercentage1), discountPercentage2: toDecimal(form.discountPercentage2), discountPercentage3: toDecimal(form.discountPercentage3),
      vatBase: t.vatBase, vatAmount: t.vatAmount, specialConsumptionTaxAmount: toDecimal(form.specialConsumptionTaxAmount),
      orderId: toNullableGuid(form.orderId), dispatchId: toNullableGuid(form.dispatchId),
    };
  };

  const buildDetailPayload = (row, index) => ({
    id: row.id || '00000000-0000-0000-0000-000000000000',
    discountAmount: toDecimal(row.discountAmount),
    discountPercentage1: toDecimal(row.discountPercentage1), discountPercentage2: toDecimal(row.discountPercentage2),
    discountPercentage3: toDecimal(row.discountPercentage3), discountPercentage4: toDecimal(row.discountPercentage4),
    discountPercentage5: toDecimal(row.discountPercentage5), discountPercentage6: toDecimal(row.discountPercentage6),
    discountPercentage7: toDecimal(row.discountPercentage7), discountPercentage8: toDecimal(row.discountPercentage8),
    grossAmount: toDecimal(row.grossAmount),
    invoiceId: isNew ? '00000000-0000-0000-0000-000000000000' : id,
    netAmount: toDecimal(row.netAmount), productId: row.productId, quantity: toDecimal(row.quantity),
    unitNetPrice: toDecimal(row.unitNetPrice),
    unitOrder: 1,
    lineOrder: index + 1,
    unit: row.unit || productUnits[row.productId]?.[0]?.name || null,
    unitPrice: toDecimal(row.unitPrice), unitVatPrice: toDecimal(row.unitVatPrice),
    vatAmount: toDecimal(row.vatAmount), vatBase: toDecimal(row.vatBase), vatPercentage: toDecimal(row.vatPercentage),
    warehouseId: row.warehouseId || form.warehouseId,
  });

  const handleSave = async ({ approve = false, lifecycle = null } = {}) => {
    if (saving) return;
    if (!form.customerId || !form.representativeId || !form.warehouseId) { showToast('error', 'Müşteri, temsilci ve depo seçimi zorunludur'); return; }
    if (!form.paymentTypeId) { showToast('error', 'Ödeme tipi seçimi zorunludur'); return; }
    if (!form.documentDate) { showToast('error', 'Fatura tarihi zorunludur'); return; }
    if (form.dueDay === '' || form.dueDay === null || form.dueDay === undefined) { showToast('error', 'Vade günü zorunludur'); return; }
    if (!form.dueDate) { showToast('error', 'Vade tarihi zorunludur'); return; }
    if (!form.shipDate) { showToast('error', 'Sevk tarihi zorunludur'); return; }
    if (form.shipDate && form.documentDate && form.shipDate < form.documentDate) {
      showToast('error', 'Sevk tarihi, fatura tarihinden önce olamaz'); return;
    }
    let rows = invoiceDetails;
    if (draftHasContent()) {
      const row = buildDraftRow(invoiceDetails.length);
      if (!row) return;
      rows = [...invoiceDetails, row];
      setInvoiceDetails(rows);
      setDraft({ ...EMPTY_DETAIL, clientId: 'draft' });
    }
    if (rows.length === 0) { showToast('error', 'En az bir fatura kalemi girilmelidir'); return; }
    const invalid = rows.find((r) => !r.productId || !(r.warehouseId || form.warehouseId) || toDecimal(r.quantity) <= 0);
    if (invalid) { showToast('error', 'Kalemlerde ürün, depo ve miktar zorunludur'); return; }
    const badRow = rows.find((r) => orderMultipleViolation(r.quantity, productMinQty[r.productId]));
    if (badRow) {
      const mb = orderMultipleViolation(badRow.quantity, productMinQty[badRow.productId]);
      showToast('error', `${productName(badRow.productId)}: miktar ${qtyLabel(mb)} ve katları olmalı (örn. ${qtyLabel(mb)}, ${qtyLabel(mb * 2)}).`);
      return;
    }
    const payload = buildPayload(rows);
    if (lifecycle !== null) payload.lifecycleStatus = lifecycle;
    if (approve) payload.approvalStatus = 1;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/SaveWithDetails`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ invoice: payload, details: rows.map(buildDetailPayload), deletedDetailIds }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.message || data?.Message || data?.error || data?.Error || 'Fatura kaydedilemedi');
      showToast('success', approve ? 'Fatura onaylandı'
        : (lifecycle === 0 ? 'Taslak kaydedildi' : (isNew ? 'Fatura oluşturuldu' : 'Fatura güncellendi')));
      setTimeout(() => navigate(listPath), 400);
    } catch (e) {
      showToast('error', e.message || 'İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (collectionAction = null) => {
    try {
      const qs = collectionAction ? `?collectionAction=${collectionAction}` : '';
      const res = await fetch(`${API_BASE}/Delete${qs}`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id }) });
      const data = await res.json().catch(() => null);
      const payload = data?.data || data?.Data;
      if (res.status === 409 && (payload?.code || payload?.Code) === 'TAHSILAT_KARARI_GEREKLI') {
        setDeleteConfirm(false);
        setCollDecision(payload.collections || payload.Collections || []);
        return;
      }
      if (!res.ok) throw new Error(data?.message || data?.Message || 'Kayıt silinemedi');
      showToast('success', 'Fatura silindi');
      navigate(listPath);
    } catch (e) { showToast('error', e.message || 'Silme sırasında hata'); setDeleteConfirm(false); setCollDecision(null); }
  };

  // ---- Filtrelenmiş kalem listesi (kolon-içi arama) ----
  const filteredDetails = useMemo(() => invoiceDetails.filter((row) => {
    const label = String(productName(row.productId));
    const code = label.split(' - ')[0];
    if (colSearch.name && !label.toLowerCase().includes(colSearch.name.toLowerCase())) return false;
    if (colSearch.code && !code.toLowerCase().includes(colSearch.code.toLowerCase())) return false;
    return true;
  }), [invoiceDetails, colSearch, productName]);
  const totalQty = invoiceDetails.reduce((sum, r) => sum + toDecimal(r.quantity), 0);

  // ---- Alan render yardımcıları (JSX bileşeni DEĞİL — odak kaybı olmasın) ----
  const Field = ({ label, k, type = 'text', wide = false, multiline = false, readOnly = false, locked = false, placeholder = '', accent = 'rgba(108,40,215,0.36)' }) => (
    <div key={k} style={{ ...st.field, ...(wide ? st.fieldWide : null) }}>
      <label style={st.label}>{label}{locked && <span style={st.lockTag} title="Sistem tarafından üretilir, değiştirilemez">oto</span>}</label>
      {multiline ? (
        <textarea
          style={{ ...editBox(accent), height: 'auto', minHeight: 64, padding: '8px 12px', resize: 'vertical' }}
          value={form[k] ?? ''} readOnly={isView || readOnly} placeholder={placeholder}
          onChange={(e) => setValue(k, e.target.value)}
        />
      ) : (
        <input
          type={type}
          style={isView || readOnly ? viewBox(accent) : editBox(accent)}
          value={form[k] ?? ''} readOnly={isView || readOnly} placeholder={placeholder}
          onChange={(e) => setValue(k, e.target.value)}
        />
      )}
    </div>
  );

  const DateField = ({ label, k, onChange, min, accent }) => (
    <div key={k} style={st.field}>
      <label style={st.label}>{label}</label>
      <input
        type="date" style={isView ? viewBox(accent) : editBox(accent)} value={form[k] ?? ''}
        readOnly={isView} min={min} onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );

  const SelectField = ({ label, k, getLabel, fetchOptions, wide = false, locked = false, onPick, disabled = false, disabledHint, accent }) => (
    <div key={k} style={{ ...st.field, ...(wide ? st.fieldWide : null) }}>
      <label style={st.label}>{label}{locked && <span style={st.lockTag} title="Distribütör kullanıcısı — kendi distribütörü dışında belge kesemez">oto</span>}</label>
      {isView || locked ? (
        <input style={viewBox(accent)} readOnly value={getLabel(form[k]) || '-'} />
      ) : (
        <SearchableSelect
          value={form[k] || ''}
          selectedLabel={form[k] ? getLabel(form[k]) : ''}
          onChange={(val) => (onPick ? onPick(val) : setValue(k, val))}
          fetchOptions={fetchOptions}
          minChars={0}
          disabled={disabled}
          placeholder={disabled ? (disabledHint || 'Önce distribütör seçin') : 'Ara / seç…'}
        />
      )}
    </div>
  );

  if (loading) {
    return <div style={s.container}><div style={{ padding: 40, color: C.muted }}>Yükleniyor…</div></div>;
  }

  const statusMeta = invoiceStatusMeta(finance.invoiceStatus);
  // Tahsilat durumu rozetinin rengi: açık = amber, kapalı = teal, fazla ödenmiş = kırmızı.
  const statusColor = finance.openAmount < 0 ? C.danger : finance.openAmount === 0 ? C.teal : C.amberStrong;
  const PURPLE_BORDER = 'rgba(108,40,215,0.36)';
  const TEAL_BORDER = 'rgba(13,155,183,0.40)';
  const BLUE_BORDER = 'rgba(39,99,234,0.49)';

  return (
    <div style={s.container} className="inv2">
      <style>{V2_FONT_CSS}</style>
      <div style={st.page}>
        {/* ① ÜST BAR */}
        <div style={st.topbar}>
          <div style={st.topLeft}>
            <div style={st.iconSquare}>🧾</div>
            <div style={{ minWidth: 0 }}>
              <p style={st.kicker}>Fatura Yönetimi · {cfg.kicker} · V2 Prototip · {isNew ? 'Yeni Kayıt' : isView ? 'Görüntüleme' : 'Düzenleme'}</p>
              <h2 style={st.title}>
                {isNew ? 'Yeni Fatura' : 'Fatura Detay'}
                {!isNew && form.textId && <span style={st.codeChip}>{form.textId}</span>}
                {!isNew && form.lifecycleStatus !== '' && <StatusBadge tone={lifecycleTone(form.lifecycleStatus)}>{lifecycleLabel(form.lifecycleStatus)}</StatusBadge>}
                {!isNew && <StatusBadge tone={approvalTone(form.approvalStatus)}>{approvalLabel(form.approvalStatus)}</StatusBadge>}
                {!isNew && (
                  <>
                    <StatusBadge tone={statusMeta.tone}>{statusMeta.label}</StatusBadge>
                    <span
                      style={{
                        ...st.codeChip,
                        background: finance.openAmount < 0 ? C.dangerSoft : C.blueSoft,
                        color: finance.openAmount < 0 ? C.danger : C.blue,
                      }}
                      title="Açık kalan tutar = Net − Eşleşen Tahsilat − İade"
                    >
                      Açık: {fmtTRY(finance.openAmount)}
                    </span>
                  </>
                )}
              </h2>
            </div>
          </div>
          <div style={st.actions}>
            {isView ? (
              <>
                {canAdd && <button style={btnSecondary} onClick={() => navigate(`${listPath}/new?variant=${variant}`)}>Yeni</button>}
                {!isNew && <button style={btnSecondary} onClick={() => setPrintOpen(true)}>Ön İzleme</button>}
                {!isNew && <button style={btnSecondary} onClick={() => setPrintOpen(true)}>Yazdır</button>}
                {fieldReturn ? (
                  <button style={btnPrimary} onClick={() => navigate(`/vendor-returns/${fieldReturn.id}`)}>Saha Belgesine Git</button>
                ) : (
                  <>
                    {canEdit && <button style={btnSecondary} onClick={() => { setEditFromUrl(false); setMode('edit'); }}>Düzenle</button>}
                    {canDelete && <button style={btnDangerV2} onClick={() => setDeleteConfirm(true)}>Sil</button>}
                  </>
                )}
                <button style={btnSecondary} onClick={() => navigate(listPath)}>Kapat</button>
                {canEdit && !fieldReturn && toInt(form.approvalStatus) !== 1 && <button style={btnPrimary} onClick={() => handleSave({ approve: true })} disabled={saving}>Faturalandır</button>}
              </>
            ) : (
              <>
                <button style={btnSecondary} onClick={() => { if (isNew || editFromUrl) navigate(listPath); else { setMode('view'); load(); } }}>İptal</button>
                {canSave && <button style={btnSecondary} onClick={() => handleSave({ lifecycle: 0 })} disabled={saving}>{saving ? 'Kaydediliyor…' : 'Taslak Kaydet'}</button>}
                {canSave && <button style={btnPrimary} onClick={() => handleSave({ lifecycle: 1 })} disabled={saving}>{saving ? 'Kaydediliyor…' : 'Kaydet'}</button>}
              </>
            )}
          </div>
        </div>

        <div style={st.scroll}>
          {/* Saha iadesi projeksiyonu — salt-okunur bandı (V1 ile aynı kural). */}
          {fieldReturn && (
            <section style={{
              display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 18px', borderRadius: 14,
              background: C.amberSoft, border: `1px solid ${C.amberStrong}`,
            }}>
              <span style={{ fontSize: 18, lineHeight: 1 }}>🔒</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.strong }}>
                  Bu fatura bir SAHA İADESİNİN muhasebe projeksiyonudur — salt okunur
                  {fieldReturn.returnNo ? ` · ${fieldReturn.returnNo}` : ''}
                </div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 4, lineHeight: 1.6 }}>
                  Belge plasiyer tarafından sahada kesildi; alış faturası dağıtımı, hasar kanıtı ve GİB durumu
                  saha belgesinde tutulur. Buradan elle değiştirilirse iki belge tutarsızlaşır.
                </div>
              </div>
              <button style={btnSecondary} onClick={() => navigate(`/vendor-returns/${fieldReturn.id}`)}>Saha Belgesine Git</button>
            </section>
          )}

          {/* ② ÜÇ RENKLİ BİLGİ KARTI */}
          <div style={st.cardRow}>
            {/* Fatura Bilgileri — mor */}
            <div style={st.cardOuter(C.purple)}>
              <div style={st.cardInner('linear-gradient(155deg,#FEFEFF,#F5F2FF)')}>
                <div style={st.cardHead}>
                  <div style={st.cardIcon(C.purple)}>📄</div>
                  <div style={st.cardTitle(C.purple)}>Fatura Bilgileri</div>
                </div>
                <div style={st.grid2}>
                  {Field({ label: 'FATURA KODU', k: 'textId', wide: true, readOnly: true, locked: true, placeholder: isNew ? 'Kayıtta otomatik atanacak' : '', accent: PURPLE_BORDER })}
                  {/* #835 — İade türü SEÇİLMEZ: menüden gelir ve belgeye sabitlenir. Künye
                      olarak salt-okunur durur ki açık belgenin hangi iade olduğu görünsün. */}
                  {isReturnVariant && (
                    <div key="returnType" style={{ ...st.field, ...st.fieldWide }}>
                      <label style={st.label}>İADE TÜRÜ</label>
                      <input style={viewBox(PURPLE_BORDER)} readOnly value={returnSubtypeLabel(form.type)} />
                    </div>
                  )}
                  {Field({ label: 'MATBU NO', k: 'printedNo', placeholder: 'Baskıda otomatik', accent: PURPLE_BORDER })}
                  <div key="dueDay" style={st.field}>
                    <label style={st.label}>VADE GÜNÜ*</label>
                    <input type="number" style={isView ? viewBox(PURPLE_BORDER) : editBox(PURPLE_BORDER)} value={form.dueDay ?? ''} readOnly={isView} placeholder="Gün" onChange={(e) => onDueDayChange(e.target.value)} />
                  </div>
                  {DateField({ label: 'FATURA TARİHİ*', k: 'documentDate', onChange: onDocDateChange, accent: PURPLE_BORDER })}
                  {DateField({ label: 'VADE TARİHİ*', k: 'dueDate', onChange: onDueDateChange, accent: PURPLE_BORDER })}
                  {DateField({ label: 'SEVK TARİHİ*', k: 'shipDate', onChange: (v) => setValue('shipDate', v), min: form.documentDate || undefined, accent: PURPLE_BORDER })}
                  {SelectField({ label: 'ÖDEME TİPİ*', k: 'paymentTypeId', getLabel: getPaymentTypeLabel, fetchOptions: paymentTypeFetch, accent: PURPLE_BORDER })}
                  <div style={{ ...st.field, ...st.fieldWide }}>
                    <label style={st.label}>
                      ETTN NO
                      <EttnQrButton ettn={ettn} buttonStyle={{ ...btnSecondary, height: 22, padding: '0 8px', fontSize: 11, marginLeft: 4 }} />
                    </label>
                    <input style={{ ...viewBox(PURPLE_BORDER), fontFamily: 'ui-monospace, monospace', fontSize: 12 }} readOnly value={ettn || (isNew ? 'Kayıttan sonra üretilecek' : '-')} />
                  </div>
                </div>
              </div>
            </div>

            {/* Müşteri ve Operasyon — teal */}
            <div style={st.cardOuter(C.teal)}>
              <div style={st.cardInner('linear-gradient(140deg,#FAFFFE,#E6FBF7)')}>
                <div style={st.cardHead}>
                  <div style={st.cardIcon(C.teal)}>👥</div>
                  <div style={st.cardTitle(C.teal)}>Müşteri ve Operasyon</div>
                </div>
                <div style={st.grid1}>
                  <div style={st.field}>
                    <label style={st.label}>MÜŞTERİ*</label>
                    {isView ? (
                      <input style={viewBox(TEAL_BORDER)} readOnly value={customerName(form.customerId)} />
                    ) : (
                      <RelationPicker
                        config={customerConfig}
                        value={form.customerId || ''}
                        selectedLabel={form.customerId ? customerName(form.customerId) : ''}
                        onChange={(val, item) => { setValue('customerId', val); if (val && item) setCustomerLabels((p) => ({ ...p, [val]: lookupLabel(item) })); if (val) applyCustomerSelection(val, item); }}
                        disabled={!form.vendorId}
                        placeholder={form.vendorId ? 'Müşteri ara…' : 'Önce distribütör seçin'}
                      />
                    )}
                  </div>
                  {SelectField({ label: 'DİSTRİBÜTÖR*', k: 'vendorId', getLabel: vendorName, fetchOptions: vendorFetch, locked: distributorUser.isDistributor, onPick: changeVendor, accent: TEAL_BORDER })}
                  {SelectField({ label: 'DEPO*', k: 'warehouseId', getLabel: warehouseName, fetchOptions: warehouseFetch, disabled: !form.vendorId, onPick: changeWarehouse, accent: TEAL_BORDER })}
                  <div style={st.grid2}>
                    {Field({ label: 'ERP ID', k: 'erpId', readOnly: true, accent: TEAL_BORDER })}
                    {Field({ label: 'SİPARİŞ NO', k: 'orderId', accent: TEAL_BORDER })}
                  </div>
                </div>
              </div>
            </div>

            {/* Satış Temsilcisi — mavi */}
            <div style={st.cardOuter(C.blue)}>
              <div style={st.cardInner('linear-gradient(142deg,#F7F8FF,#DBEDFF)')}>
                <div style={st.cardHead}>
                  <div style={st.cardIcon(C.blue)}>🧑‍💼</div>
                  <div style={st.cardTitle(C.blue)}>Satış Temsilcisi</div>
                </div>
                <div style={st.grid1}>
                  {SelectField({ label: 'TEMSİLCİ*', k: 'representativeId', getLabel: representativeName, fetchOptions: repFetch, disabled: !form.vendorId, accent: BLUE_BORDER })}
                  <div style={st.field}>
                    <label style={st.label}>BÖLGE/EKİP</label>
                    <input style={viewBox(BLUE_BORDER)} readOnly value={representativeRegion(form.representativeId) || '-'} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ③ DAHA FAZLA */}
          <div style={st.moreBar} onClick={() => setMoreOpen((v) => !v)}>
            <span style={{ color: C.navy, fontSize: 15, transform: moreOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>❯</span>
            Daha Fazla ( Makbuz, E-Arşiv, Özel Kod, Teslimat, Açıklamalar, Genel İskonto)
          </div>
          {moreOpen && (
            <section style={st.moreSection}>
              <div style={st.moreGrid}>
                {Field({ label: 'MAKBUZ NO', k: 'receiptNo' })}
                {Field({ label: 'E-ARŞİV', k: 'eArchive' })}
                {Field({ label: 'ÖZEL KOD', k: 'specialId' })}
                {Field({ label: 'DISPATCH ID', k: 'dispatchId', readOnly: true })}
                {Field({ label: 'GENEL İSKONTO %1', k: 'discountPercentage1', type: 'number' })}
                {Field({ label: 'GENEL İSKONTO %2', k: 'discountPercentage2', type: 'number' })}
                {Field({ label: 'GENEL İSKONTO %3', k: 'discountPercentage3', type: 'number' })}
                {Field({ label: 'ÖTV TUTARI', k: 'specialConsumptionTaxAmount', type: 'number' })}
                {Field({ label: 'YIL', k: 'year', type: 'number' })}
                {Field({ label: 'TESLİMAT ADRESİ', k: 'deliveryAdress', wide: true, multiline: true })}
                {Field({ label: 'AÇIKLAMA 1', k: 'description1', wide: true, multiline: true })}
                {Field({ label: 'AÇIKLAMA 2', k: 'description2', wide: true, multiline: true })}
              </div>
            </section>
          )}

          {/* ④ İLİŞKİLİ TAHSİLATLAR */}
          {!isNew && (
            <section style={st.panel}>
              <div style={st.panelHead}>
                <div style={st.panelIcon(C.amberSoft, C.amber)}>💳</div>
                <div style={st.panelTitle(C.amber)}>İlişkili Tahsilatlar</div>
              </div>

              <div style={st.allocGrid}>
                <div style={st.allocSummary}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                    <div>
                      <div style={st.allocKicker}>FATURA</div>
                      <div style={{ marginTop: 8, fontSize: 19, fontWeight: 700, color: C.strong }}>{form.textId || form.printedNo || '-'}</div>
                    </div>
                    <div style={st.allocStatus(statusColor)}>{statusMeta.label}</div>
                  </div>
                  <div style={st.allocRule} />
                  <div style={st.allocKicker}>TUTAR</div>
                  <div style={st.allocValue}>{fmtTRY(finance.netAmount)}</div>
                  {form.dueDate && <div style={{ marginTop: 14, fontSize: 13, color: C.muted }}>Vade: {fmtTRDate(form.dueDate)}</div>}
                  {allocations.length === 0 && returnInvoices.length === 0 && (
                    <div style={{ marginTop: 16, fontSize: 13, fontStyle: 'italic', color: '#8A8A8A' }}>
                      Bu faturaya henüz tahsilat eşleşmemiş.
                    </div>
                  )}
                </div>

                <div style={st.statRows}>
                  <div style={st.statBox('#F1F6FF')}>
                    <div style={st.statLabel(C.blue)}>FATURA</div>
                    <div style={st.statValue}>{fmtTRY(finance.netAmount)}</div>
                  </div>
                  <div style={st.statBox('#F4FAF9')}>
                    <div style={st.statLabel(C.teal)}>EŞLEŞEN TAHSİLAT</div>
                    <div style={st.statValue}>
                      {fmtTRY(finance.allocatedAmount)}
                      {finance.returnedAmount > 0 && (
                        <span style={{ marginLeft: 10, fontSize: 13, fontWeight: 600, color: C.amber }}>+ iade {fmtTRY(finance.returnedAmount)}</span>
                      )}
                    </div>
                  </div>
                  <div style={st.statBox(finance.openAmount < 0 ? C.dangerSoft : '#FEF5F6')}>
                    <div style={st.statLabel(C.danger)}>AÇIK TUTAR</div>
                    <div style={st.statValue}>
                      {fmtTRY(finance.openAmount)}
                      {finance.openAmount < 0 && (
                        <span style={{ marginLeft: 10, fontSize: 12, fontWeight: 700, color: C.danger }}>FAZLA ÖDENMİŞ — eşleştirmeyi düzeltin</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Eşleşme satırları — tasarımda tek özet kutusu var; detay listesi V1'den korunur. */}
              {(allocations.length > 0 || returnInvoices.length > 0) && (
                <div style={st.allocList}>
                  {allocations.map((a, idx) => {
                    const key = a.financeTransactionId ? `ft:${a.financeTransactionId}` : `pay:${a.paymentId}`;
                    const src = allocSources[key];
                    const isLegacy = !!a.paymentId;
                    const detailRoute = src && !isLegacy
                      ? transactionDetailRoute(src.direction, src.method, a.financeTransactionId)
                      : null;
                    return (
                      <div key={a.id || `${key}-${idx}`} style={st.allocRow}>
                        {detailRoute ? (
                          <button type="button" style={st.allocLink} onClick={() => navigate(detailRoute)} title="Tahsilat detayına git">
                            {src.label}
                          </button>
                        ) : (
                          <b>{src?.label || 'Tahsilat'}</b>
                        )}
                        <span style={{ color: C.muted }}>→</span>
                        <b>{fmtTRY(a.allocatedAmount)}</b>
                        <span style={{ color: C.muted }}>({fmtTRDate(src?.date || a.allocatedAt)})</span>
                        <span style={st.tagSoft}>{ALLOCATION_SOURCE_LABELS[a.allocatedBy] || '-'}</span>
                        {isLegacy && <span style={st.tagGrey}>Legacy Ödeme</span>}
                        {a.note && <span style={st.note} title={a.note}>Gerekçe: {a.note}</span>}
                      </div>
                    );
                  })}
                  {returnInvoices.map((r) => (
                    <div key={r.id} style={st.allocRow}>
                      <button type="button" style={st.allocLink} onClick={() => navigate(`/invoices/${r.id}`)}>
                        İade {r.invoiceNo}
                      </button>
                      <span style={{ color: C.muted }}>→</span>
                      <b>{fmtTRY(r.netAmount)}</b>
                      <span style={{ color: C.muted }}>({fmtTRDate(r.documentDate)})</span>
                      <span style={st.tagAmber}>iade</span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* ⑤ FATURA KALEMLERİ */}
          <div style={st.itemsCard}>
            <div style={st.panelHead}>
              <div style={st.panelIcon(C.blueSoft, C.navy)}>K</div>
              <div style={st.panelTitle(C.navy)}>Fatura Kalemleri</div>
              <div style={st.countPill}>{filteredDetails.length}/{invoiceDetails.length} kalem · {totalQty.toLocaleString('tr-TR')} adet</div>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ ...st.searchBox, width: 230 }}>
                  <span style={{ color: '#8A96AE', fontSize: 13 }}>🔍</span>
                  <input style={st.searchInput} placeholder="Ürün Ara" value={colSearch.name} onChange={(e) => setColSearch((c) => ({ ...c, name: e.target.value }))} />
                </div>
                <div style={{ ...st.searchBox, width: 190 }}>
                  <span style={{ color: '#8A96AE', fontSize: 13 }}>🔍</span>
                  <input style={st.searchInput} placeholder="Kod Ara" value={colSearch.code} onChange={(e) => setColSearch((c) => ({ ...c, code: e.target.value }))} />
                </div>
              </div>
            </div>

            <div ref={tableWrapRef} style={st.tableWrap}>
              <table style={st.table}>
                <thead>
                  <tr>
                    <th style={{ ...st.th, textAlign: 'center', width: 46, borderRadius: '20px 0 0 0' }}>#</th>
                    <th style={{ ...st.th, ...st.thLeft }}>ÜRÜN KODU</th>
                    <th style={{ ...st.th, ...st.thLeft }}>ÜRÜN AÇIKLAMASI</th>
                    <th style={st.th}>MİKTAR</th>
                    <th style={{ ...st.th, ...st.thLeft }}>BİRİM</th>
                    <th style={st.th} title="Belgede seçili depodaki kullanılabilir stok (satırın birimine çevrilmiş)">STOK</th>
                    <th style={st.th}>BİRİM FİYAT</th>
                    <th style={st.th}>İSK-1%</th>
                    <th style={st.th}>İSK-2%</th>
                    <th style={st.th}>İSK-3%</th>
                    <th style={st.th}>İSK-4%</th>
                    <th style={st.th}>İSKONTO ₺</th>
                    <th style={st.th}>NET</th>
                    <th style={st.th}>KDV%</th>
                    <th style={st.th}>KDV ₺</th>
                    <th style={{ ...st.th, ...(isView ? { borderRadius: '0 20px 0 0' } : null) }}>BRÜT</th>
                    {!isView && <th style={{ ...st.th, width: 48, borderRadius: '0 20px 0 0' }} />}
                  </tr>
                </thead>
                <tbody>
                  {filteredDetails.length === 0 && (
                    <tr><td colSpan={isView ? 16 : 17} style={{ ...st.td, textAlign: 'center', color: C.muted, padding: 28 }}>Kalem yok — aşağıdan ürün ekleyin.</td></tr>
                  )}
                  {filteredDetails.map((row, idx) => {
                    const label = String(productName(row.productId));
                    const code = label.split(' - ')[0];
                    const desc = label.split(' - ').slice(1).join(' - ') || label;
                    return (
                      <tr key={row.clientId}>
                        <td style={{ ...st.td, textAlign: 'center', color: C.muted }}>{idx + 1}</td>
                        <td style={{ ...st.td, ...st.tdLeft }}>{row.productId ? <span style={st.codeCell}>{code}</span> : '-'}</td>
                        <td style={{ ...st.td, ...st.tdLeft, whiteSpace: 'normal', maxWidth: 290, lineHeight: 1.45 }}>{desc}</td>
                        <td style={st.td}>{isView ? <b>{row.quantity}</b> : <input inputMode="decimal" style={st.cellInput} value={row.quantity} onChange={(e) => updateDetailRow(row.clientId, 'quantity', sanitizeDecimal(e.target.value))} />}</td>
                        <td style={{ ...st.td, ...st.tdLeft }}>{renderUnitCell(row.productId, row.unit, (v) => changeRowUnit(row, v))}</td>
                        <td style={st.td}>{renderStockCell(row.productId, row.unit)}</td>
                        <td style={st.td}>{isView ? formatMoney(row.unitPrice) : <input inputMode="decimal" style={st.cellInput} value={row.unitPrice} onChange={(e) => updateDetailRow(row.clientId, 'unitPrice', sanitizeDecimal(e.target.value))} title="Yetkiye bağlı düzenlenebilir" />}</td>
                        {[1, 2, 3, 4].map((n) => (
                          <td style={st.td} key={n}>{isView ? formatPercent(row[`discountPercentage${n}`]) : <input inputMode="decimal" style={st.iskInput} value={row[`discountPercentage${n}`] ?? ''} placeholder="0" onChange={(e) => updateDetailRow(row.clientId, `discountPercentage${n}`, sanitizeDecimal(e.target.value))} />}</td>
                        ))}
                        <td style={st.td}>{formatMoney(row.discountAmount)}</td>
                        <td style={{ ...st.td, ...st.netCell }}>{formatMoney(row.vatBase)}</td>
                        <td style={st.td}>{isView ? formatPercent(row.vatPercentage) : <input inputMode="decimal" style={st.iskInput} value={row.vatPercentage} onChange={(e) => updateDetailRow(row.clientId, 'vatPercentage', sanitizeDecimal(e.target.value))} />}</td>
                        <td style={st.td}>{formatMoney(row.vatAmount)}</td>
                        <td style={st.td}>{formatMoney(row.grossAmount)}</td>
                        {!isView && <td style={st.td}><button type="button" style={rowDeleteBtn} onClick={() => removeDetailRow(row)}>×</button></td>}
                      </tr>
                    );
                  })}
                </tbody>
                {!isView && (
                  <tfoot>
                    <tr style={{ position: 'sticky', bottom: 0, background: '#fff', boxShadow: '0 -1px 0 rgba(102,117,140,0.25)' }}>
                      <td style={{ ...st.td, textAlign: 'center', color: C.blue, fontWeight: 700 }}>＋</td>
                      <td style={{ ...st.td, ...st.tdLeft }} colSpan={2}>
                        <RelationPicker
                          config={productConfig}
                          value={draft.productId || ''}
                          selectedLabel={draft.productId ? productName(draft.productId) : ''}
                          onChange={(val, item) => setDraftProduct(val, item)}
                          placeholder={headerReady ? 'Ürün kodu yaz / ara…' : headerHint}
                          panelMinWidth={860}
                          selectedCode={draft.productId ? String(productName(draft.productId)).split(' - ')[0] : ''}
                          openToken={productPickerOpenToken}
                          disabled={!headerReady}
                        />
                        {!headerReady && (
                          <span style={{ display: 'block', marginTop: 6, color: C.muted, fontSize: 12 }}>{headerHint}.</span>
                        )}
                      </td>
                      <td style={st.td}><input ref={qtyRef} inputMode="decimal" style={st.cellInput} value={draft.quantity} onChange={(e) => setDraft((d) => recalcLine({ ...d, quantity: sanitizeDecimal(e.target.value) }))} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); unitPriceRef.current?.focus(); } }} /></td>
                      <td style={{ ...st.td, ...st.tdLeft }}>{renderUnitCell(draft.productId, draft.unit, (v) => changeDraftUnit(v))}</td>
                      <td style={st.td}>{renderStockCell(draft.productId, draft.unit)}</td>
                      <td style={st.td}><input ref={unitPriceRef} inputMode="decimal" style={st.cellInput} value={draft.unitPrice} onChange={(e) => setDraft((d) => recalcLine({ ...d, unitPrice: sanitizeDecimal(e.target.value) }))} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); isk1Ref.current?.focus(); } }} /></td>
                      <td style={st.td}><input ref={isk1Ref} inputMode="decimal" style={st.iskInput} value={draft.discountPercentage1} placeholder="0" onChange={(e) => setDraft((d) => recalcLine({ ...d, discountPercentage1: sanitizeDecimal(e.target.value) }))} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitDraft(); } }} /></td>
                      <td style={st.td}><input inputMode="decimal" style={st.iskInput} value={draft.discountPercentage2} placeholder="0" onChange={(e) => setDraft((d) => recalcLine({ ...d, discountPercentage2: sanitizeDecimal(e.target.value) }))} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitDraft(); } }} /></td>
                      <td style={st.td}><input inputMode="decimal" style={st.iskInput} value={draft.discountPercentage3} placeholder="0" onChange={(e) => setDraft((d) => recalcLine({ ...d, discountPercentage3: sanitizeDecimal(e.target.value) }))} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitDraft(); } }} /></td>
                      <td style={st.td}><input inputMode="decimal" style={st.iskInput} value={draft.discountPercentage4} placeholder="0" onChange={(e) => setDraft((d) => recalcLine({ ...d, discountPercentage4: sanitizeDecimal(e.target.value) }))} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitDraft(); } }} /></td>
                      <td style={{ ...st.td, color: C.muted, fontSize: 11 }} colSpan={2}>Enter ile satırı ekle</td>
                      <td style={st.td}><input inputMode="decimal" style={st.iskInput} value={draft.vatPercentage} onChange={(e) => setDraft((d) => recalcLine({ ...d, vatPercentage: sanitizeDecimal(e.target.value) }))} /></td>
                      <td style={st.td} />
                      <td style={st.td} colSpan={2}><button type="button" style={{ ...btnPrimary, height: 34 }} onClick={commitDraft}>+ Ekle</button></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </div>

        {/* ⑥ ALT SABİT TOPLAM BARI */}
        <div style={st.totbar}>
          <div style={st.totCells}>
            <div style={st.totCell(true)}>
              <div style={st.totLabel}>BRÜT TUTAR</div>
              <div style={st.totValue}>{formatMoney(totals.grossAmount)}</div>
            </div>
            <div style={st.totCell(false)}>
              <div style={st.totLabel}>İSKONTO</div>
              <div style={{ ...st.totValue, color: C.danger }}>−{formatMoney(totals.discountAmount)}</div>
            </div>
            <div style={st.totCell(false)}>
              <div style={st.totLabel}>NET(KDV HARİÇ)</div>
              <div style={st.totValue}>{formatMoney(totals.vatBase)}</div>
            </div>
            <div style={st.totCell(false)}>
              <div style={st.totLabel}>KDV TOPLAMI</div>
              <div style={st.totValue}>{formatMoney(totals.vatAmount)}</div>
            </div>
            {totals.sct > 0 && (
              <div style={st.totCell(false)}>
                <div style={st.totLabel}>ÖTV</div>
                <div style={st.totValue}>{formatMoney(totals.sct)}</div>
              </div>
            )}
            <button
              type="button" disabled={isNew}
              style={{ ...st.totCell(false), background: 'transparent', border: 'none', borderLeft: '1px solid rgba(255,255,255,0.22)', textAlign: 'left', cursor: isNew ? 'default' : 'pointer', fontFamily: 'inherit' }}
              onClick={() => { if (!isNew) setPaymentDrawer(true); }}
              title={isNew ? 'Tahsilat kaydı yok' : 'Ödeme detayları'}
            >
              <div style={st.totLabel}>TAHSİLAT DURUMU{!isNew && ' ▸'}</div>
              <div style={{
                ...st.totValue,
                color: collection.tone === 'ok' ? '#3DDC97' : collection.tone === 'bad' ? C.danger : collection.tone === 'wait' ? C.amberStrong : '#C9CEE6',
              }}
              >
                {isNew ? '—' : `${formatPercent(collection.percent)} · ${collection.label}`}
              </div>
            </button>
          </div>
          <div style={st.grandCell}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4 }}>GENEL TOPLAM</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{formatMoney(totals.netAmount)}</div>
          </div>
        </div>
      </div>

      {/* Tahsilat drawer */}
      {paymentDrawer && (
        <div style={s.overlay} onClick={() => setPaymentDrawer(false)}>
          <div style={{ ...s.modal, maxWidth: 760 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Fatura Ödemeleri — {form.textId || form.printedNo || '-'}</h3>
              <button style={s.closeBtn} onClick={() => setPaymentDrawer(false)}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                <div><div style={{ color: C.muted, fontSize: 12, fontWeight: 700 }}>Fatura Toplamı</div><div style={{ fontSize: 16, fontWeight: 800, color: C.strong }}>{formatMoney(totals.netAmount)}</div></div>
                <div><div style={{ color: C.muted, fontSize: 12, fontWeight: 700 }}>Toplam Ödeme</div><div style={{ fontSize: 16, fontWeight: 800, color: C.strong }}>{formatMoney(paidTotal)}</div></div>
                <div><div style={{ color: C.muted, fontSize: 12, fontWeight: 700 }}>Kalan</div><div style={{ fontSize: 16, fontWeight: 800, color: C.strong }}>{formatMoney(Math.max(0, totals.netAmount - paidTotal))}</div></div>
              </div>
              <div style={{ border: `1px solid ${C.cardBorder}`, borderRadius: 8, overflow: 'hidden', marginTop: 14 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1.1fr 1fr 0.9fr 1fr', gap: 8, padding: '10px 12px', background: '#F4F8FF', color: C.muted, fontSize: 12, fontWeight: 800, textTransform: 'uppercase' }}>
                  <div>Ödeme No</div><div>Tarih</div><div>Banka</div><div>Makbuz</div><div style={{ textAlign: 'right' }}>Tutar</div>
                </div>
                {payments.length === 0 ? (
                  <div style={{ padding: 16, color: C.muted, fontSize: 13 }}>Bu faturaya ait ödeme kaydı bulunmuyor.</div>
                ) : payments.map((p) => (
                  <div key={p.id} style={{ display: 'grid', gridTemplateColumns: '1.1fr 1.1fr 1fr 0.9fr 1fr', gap: 8, padding: 12, borderTop: `1px solid ${C.cardBorder}`, fontSize: 13 }}>
                    <div>{p.textId || '-'}</div><div>{displayDate(p.paymentDate)}</div><div>{p.bankName || '-'}</div><div>{p.receiptNo || '-'}</div>
                    <div style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoney(p.amount)}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={s.modalFooter}><button style={s.btnSecondary} onClick={() => setPaymentDrawer(false)}>Kapat</button></div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: C.danger }}>Silme Onayı</h3></div>
            <div style={s.modalBody}><p style={{ margin: 0, lineHeight: 1.6 }}>Bu faturayı silmek istediğinize emin misiniz?</p></div>
            <div style={s.modalFooter}><button style={s.btnDanger} onClick={() => handleDelete()}>Evet, Sil</button><button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>İptal</button></div>
          </div>
        </div>
      )}

      {collDecision && (
        <div style={s.overlay} onClick={() => setCollDecision(null)}>
          <div style={{ ...s.modal, maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: C.danger }}>Bağlı Tahsilat Var</h3></div>
            <div style={s.modalBody}>
              <p style={{ margin: '0 0 10px', lineHeight: 1.6 }}>
                Bu belge aşağıdaki tahsilat(lar) ile kapatılmış. Tahsilat kaydını da iptale almak ister misiniz?
              </p>
              <ul style={{ margin: '0 0 4px', paddingLeft: 18 }}>
                {collDecision.map((c, i) => (
                  <li key={i} style={{ marginBottom: 4 }}>
                    {(c.documentNo || c.DocumentNo) ? `${c.documentNo || c.DocumentNo} — ` : ''}
                    {Number(c.amount ?? c.Amount ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                  </li>
                ))}
              </ul>
              <p style={{ margin: '8px 0 0', color: C.muted, fontSize: 13, lineHeight: 1.5 }}>
                <strong>Hayır</strong> derseniz belge silinir, tahsilat ayakta kalır ve avans tahsilata döner.
              </p>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={() => handleDelete('cancel')}>Evet, tahsilat da iptal edilsin</button>
              <button style={s.btnSecondary} onClick={() => handleDelete('keep')}>Hayır, yalnız belgeyi sil</button>
            </div>
          </div>
        </div>
      )}

      <DocumentPrintDialog
        open={printOpen}
        onClose={() => setPrintOpen(false)}
        docType={2}
        documentTip={documentTip}
        year={toInt(form.year)}
        documentId={id}
        title={form.textId}
      />

      <Toast toast={toast} />
    </div>
  );
}

export default InvoiceDetailV2Page;
